import { motion, useReducedMotion } from "motion/react";
import { useState } from "react";

import {
  ArrowIcon,
  GitHubIcon,
  LinkedInIcon,
  DownloadIcon,
  LocationIcon,
  MailIcon,
  WhatsAppIcon,
} from "../components/Icons";
import { Reveal, RevealItem, stagger } from "../components/Reveal";
import { Shell } from "../components/Shell";
import { Failed, Loading } from "../components/States";
import { useApi } from "../hooks/useApi";
import { api } from "../lib/api";
import { downloadable } from "../lib/downloads";

/**
 * Contact — every route in, on one page.
 *
 * The direct links come first and are the largest thing here. A form is
 * something a visitor has to trust; an address they can copy is not, and a
 * recruiter with four minutes should never have to pick the slower option.
 *
 * Icons are decoration on top of text labels, never instead of them. Every
 * card says "Email" or "LinkedIn" in words — an envelope glyph means "email"
 * only to someone who already knows — and the icons are `aria-hidden`, so a
 * screen reader hears the label once rather than twice.
 */

/**
 * International format, digits only — wa.me rejects spaces and a leading +.
 *
 * Held here rather than read from the API: the phone number is deliberately
 * kept out of the public endpoints (DATA-NOTES), and a wa.me link is a
 * different exposure decision from publishing a number to call.
 */
const WHATSAPP_NUMBER: string | null = "923364478721";

function field(data: FormData, name: string): string {
  // FormData entries are `string | File`. Stringifying a File yields
  // "[object Object]", which would be posted as someone's name.
  const value = data.get(name);
  return typeof value === "string" ? value : "";
}

type Status =
  | { kind: "idle" }
  | { kind: "sending" }
  | { kind: "sent" }
  | { kind: "failed"; message: string; problems: Record<string, string> };

interface Channel {
  label: string;
  value: string;
  href: string;
  icon: typeof MailIcon;
  external?: boolean;
}

function ChannelCard({ channel }: { channel: Channel }) {
  const reduced = useReducedMotion() ?? false;
  const Icon = channel.icon;

  return (
    <motion.a
      href={channel.href}
      target={channel.external ? "_blank" : undefined}
      rel={channel.external ? "noreferrer" : undefined}
      whileHover={reduced ? undefined : { y: -6 }}
      transition={{ type: "spring", stiffness: 240, damping: 20 }}
      className="glass group relative flex h-full flex-col items-start gap-5 overflow-hidden rounded-2xl border border-edge p-6 transition-colors duration-300 hover:border-accent-dim sm:p-7"
    >
      {/* A bloom that only arrives on hover, behind everything. Static glow on
          six tiles at once is noise; one lighting up is a response. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -top-16 -right-16 size-40 rounded-full bg-glow/25 opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100"
      />

      {/* The mark. Large enough to be the thing you aim at, with the label
          underneath — the icon is recognition, the words are the meaning. */}
      <span className="relative flex size-14 items-center justify-center rounded-2xl border border-edge bg-canvas text-accent transition-colors duration-300 group-hover:border-accent-dim">
        <Icon className="size-7" />
      </span>

      <span className="relative min-w-0">
        <span className="block text-base text-text">{channel.label}</span>
        <span className="mt-1 block truncate text-2xs text-text-dim">{channel.value}</span>
      </span>

      <ArrowIcon
        aria-hidden="true"
        className="relative mt-auto size-4 -rotate-45 text-text-dim transition-all duration-300 group-hover:translate-x-0.5 group-hover:text-accent"
      />
    </motion.a>
  );
}

export function Contact() {
  const profile = useApi(() => api.profile(), []);
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  async function handleSubmit(event: React.SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setStatus({ kind: "sending" });

    try {
      const response = await fetch("/api/v1/public/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: field(data, "name"),
          email: field(data, "email"),
          company: field(data, "company") || null,
          message: field(data, "message"),
        }),
      });

      if (!response.ok) {
        const body = (await response.json()) as {
          error: { message: string; problems?: { field: string; message: string }[] };
        };
        const problems = Object.fromEntries(
          (body.error.problems ?? []).map((problem) => [problem.field, problem.message]),
        );
        setStatus({ kind: "failed", message: body.error.message, problems });
        return;
      }

      form.reset();
      setStatus({ kind: "sent" });
    } catch {
      setStatus({
        kind: "failed",
        message: "Couldn't reach the server. Your message wasn't sent — try again in a moment.",
        problems: {},
      });
    }
  }

  if (profile.loading)
    return (
      <Shell className="py-24">
        <Loading label="this page" />
      </Shell>
    );
  if (profile.error)
    return (
      <Shell className="py-24">
        <Failed error={profile.error} what="this page" />
      </Shell>
    );
  if (!profile.data) return null;

  const { email, github_url, linkedin_url, location, resume_url } = profile.data;
  const failed = status.kind === "failed" ? status : null;

  const channels: Channel[] = [
    { label: "Email", value: email, href: `mailto:${email}`, icon: MailIcon },
  ];
  if (linkedin_url) {
    channels.push({
      label: "LinkedIn",
      value: "Connect and message",
      href: linkedin_url,
      icon: LinkedInIcon,
      external: true,
    });
  }
  if (github_url) {
    channels.push({
      label: "GitHub",
      value: "Code and repositories",
      href: github_url,
      icon: GitHubIcon,
      external: true,
    });
  }
  if (resume_url) {
    channels.push({
      label: "Résumé",
      value: "Download the PDF",
      href: downloadable(resume_url),
      icon: DownloadIcon,
    });
  }
  if (WHATSAPP_NUMBER) {
    channels.push({
      label: "WhatsApp",
      value: "+92 336 4478721",
      href: `https://wa.me/${WHATSAPP_NUMBER}`,
      icon: WhatsAppIcon,
      external: true,
    });
  }

  return (
    <Shell className="py-24">
      <p className="mb-3 font-mono text-2xs tracking-[0.28em] text-accent uppercase">Contact</p>
      <h1 className="mb-6 text-xl sm:text-2xl lg:text-3xl">Get in touch</h1>
      <p className="mb-14 max-w-(--spacing-measure) text-lg text-text-dim">
        Email is fastest. The form does the same thing with more steps.
      </p>

      <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {channels.map((channel, index) => (
          <RevealItem key={channel.label} delay={stagger(index)}>
            <ChannelCard channel={channel} />
          </RevealItem>
        ))}
      </ul>

      {location && (
        <p className="mt-6 flex items-center gap-2.5 text-2xs text-text-dim">
          <LocationIcon className="size-4 text-accent" />
          {location}
        </p>
      )}

      <Reveal className="mt-20">
        <hr className="hairline mb-16 border-0" />
        <h2 className="mb-8 text-lg">Or send a message</h2>

        {status.kind === "sent" ? (
          <p role="status" className="max-w-(--spacing-measure) text-base text-text">
            Thanks — that&rsquo;s reached his inbox. He&rsquo;ll reply to the address you gave.
          </p>
        ) : (
          <form
            onSubmit={(event) => void handleSubmit(event)}
            className="max-w-xl space-y-6"
            noValidate
          >
            {failed && (
              <p role="alert" className="text-base text-halt">
                {failed.message}
              </p>
            )}

            <Field name="name" label="Your name" required error={failed?.problems.name} />
            <Field
              name="email"
              label="Email"
              type="email"
              required
              error={failed?.problems.email}
            />
            <Field name="company" label="Company (optional)" error={failed?.problems.company} />
            <Field
              name="message"
              label="Message"
              multiline
              required
              error={failed?.problems.message}
            />

            {/* Bordered, not filled. A solid accent button is the fastest way
                to make either palette look cheap. */}
            <button
              type="submit"
              disabled={status.kind === "sending"}
              className="group inline-flex items-center gap-2 rounded-full border border-accent-dim px-6 py-3 text-xs text-accent transition-colors duration-300 hover:border-accent hover:bg-accent/10 disabled:opacity-50"
            >
              {status.kind === "sending" ? "Sending…" : "Send message"}
              <ArrowIcon className="size-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
            </button>
          </form>
        )}
      </Reveal>
    </Shell>
  );
}

function Field({
  name,
  label,
  type = "text",
  required = false,
  multiline = false,
  error,
}: {
  name: string;
  label: string;
  type?: string;
  required?: boolean;
  multiline?: boolean;
  error?: string;
}) {
  const errorId = `${name}-error`;
  const shared = {
    id: name,
    name,
    required,
    // Points the screen reader at the message rather than leaving it to
    // notice red text somewhere nearby.
    "aria-describedby": error ? errorId : undefined,
    "aria-invalid": error ? true : undefined,
    // --line rather than --edge: an input's border is the only thing saying
    // "you can type here", which makes it a boundary WCAG 2.2 SC 1.4.11
    // requires at 3:1. --edge is decorative and nowhere near that.
    className: `w-full rounded-xl border bg-surface px-4 py-2.5 text-base text-text transition-colors duration-200 ${
      error ? "border-halt" : "border-line focus:border-accent"
    }`,
  } as const;

  return (
    <div>
      <label htmlFor={name} className="mb-2 block text-xs text-text-dim">
        {label}
      </label>
      {multiline ? <textarea {...shared} rows={5} /> : <input {...shared} type={type} />}
      {error && (
        <p id={errorId} className="mt-2 text-2xs text-halt">
          {error}
        </p>
      )}
    </div>
  );
}
